import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import messagesCountQuery from '@aurora/shared-client/components/messages/MessagesCount.query.graphql';
import MessageStatusLabel from '@aurora/shared-client/components/messages/MessageStatusLabel/MessageStatusLabel';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import type { BoardPagesAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type { MessageConstraints } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  IdeaStatusDetailsFragment,
  MessagesCountQuery,
  MessagesCountQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import { EndUserComponent, EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { getBoardViewAllPageAndQueryPathParams } from '../../../../helpers/boards/ConversationStyleBehaviorHelper';
import type { IdeaStatusViewVariant, ItemType } from '../../../../types/enums';
import type { ItemViewVariantFC } from '../../../entities/types';
import useTranslation from '../../../useTranslation';
import localStyles from './IdeaStatusViewInline.module.pcss';

/**
 * Idea Status View - Inline
 *
 * @author rishabh.jivan
 */
const IdeaStatusViewInline: ItemViewVariantFC<
  IdeaStatusDetailsFragment,
  ItemType.IDEA_STATUS,
  IdeaStatusViewVariant.INLINE
> = ({ entity: ideaStatus, className, showIdeaCount }) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage } = useTranslation(EndUserComponent.IDEA_STATUS_VIEW_INLINE);
  const { contextNode } = useContext(AppContext);
  const { Link } = useEndUserRoutes();

  const constraints: MessageConstraints = {
    boardId: { eq: contextNode.id },
    statusKey: { eq: ideaStatus.statusKey },
    depth: { eq: 0 }
  };
  const { data: messageCountResult, loading: ideaCountLoading } = useQueryWithTracing<
    MessagesCountQuery,
    MessagesCountQueryVariables
  >(module, messagesCountQuery, {
    variables: {
      constraints
    },
    skip: !showIdeaCount
  });

  if (ideaCountLoading) {
    return null;
  }

  const ideasCount = messageCountResult && messageCountResult.messagesCount;
  const { page, pathParams } = getBoardViewAllPageAndQueryPathParams(contextNode);

  /**
   * Renders the idea status pill
   */
  function renderStatusPill(): React.ReactElement {
    return <MessageStatusLabel status={ideaStatus} className={cx('lia-featured-status-pill')} />;
  }

  /**
   * Renders the number of idea posts which are in the given status
   */
  function renderIdeaCount(): React.ReactElement {
    return (
      <span className={cx('lia-featured-status-count')} data-testid="IdeaCount">
        {formatMessage('ideaCount', { ideaCount: ideasCount ?? 0 })}
      </span>
    );
  }

  /**
   * Renders the idea status list item
   */
  function renderListItem(): React.ReactElement {
    return (
      <div data-testid="IdeaStatusViewInline" className={cx('lia-featured-status', className)}>
        {renderStatusPill()}
        {showIdeaCount && renderIdeaCount()}
      </div>
    );
  }

  return (
    <Link<BoardPagesAndParams>
      legacyBehavior={true}
      route={page}
      params={pathParams}
      query={{ [EndUserQueryParams.SEARCH_FILTER_BY_MESSAGE_STATUS]: ideaStatus.statusKey }}
      passHref
    >
      {renderListItem()}
    </Link>
  );
};

export default IdeaStatusViewInline;
